import * as E from 'fp-ts/lib/Either';
import * as t from 'io-ts';

import {CustomError, ErrorWithData} from '../../../libs/shared/helpers/src/types/customErrors';
import {DataStateForBoot} from '../../../src/store/data/dataState';
import {FeatureFlagsState} from '../../../src/store/featureFlags/featureFlagsState';
import {MetadataState} from '../../../src/store/metadata/metadataState';

export interface PersistedState {
  originalData: DataStateForBoot;
  metadata: Pick<MetadataState, 'cellsLists'>;
  featureFlags: FeatureFlagsState;
}

export type GetField<TState> = (state: PersistedState) => TState;

export type GetPersistedState = () => Promise<PersistedState | undefined>;

export interface PersistedStateProvider {
  loadInitialPersistedStateAsync: GetPersistedState;
  tryClearPersistedStateAsync: () => Promise<void>;
}

export interface DeferredPersistedState {
  originalData: Pick<PersistedState['originalData'], 'activities' | 'comments' | 'messages'>;
}

/*
 * Helpers.
 */

export class PersistedStorageEmptyError extends Error {
  constructor() {
    super('Persisted store is empty');
  }
}

export class PersistedStorageUnsupportedError extends CustomError {
  constructor() {
    super('No persisted storage available');
  }
}

export class PersistedStorageTypeValidationError extends ErrorWithData<PersistedStorageTypeValidationErrorData> {
  constructor(data: PersistedStorageTypeValidationErrorData) {
    super('PersistedState: Unable to parse model', data);
  }
}

export interface PersistedStorageTypeValidationErrorData {
  name: string;
  errors: t.Errors;
}

export function validateModel<T>(runtimeType: t.Decoder<unknown, T>, model: any): T {
  const modelValidated = runtimeType.decode(model);

  // Are we able to validate the model?
  if (!E.isRight(modelValidated)) {
    throw new PersistedStorageTypeValidationError({
      name: runtimeType.name,
      errors: modelValidated.left,
    });
  }

  return modelValidated.right;
}
